import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

if (!customElements.get('pdp-total-cost')) {
  customElements.define(
    'pdp-total-cost',
    class TotalPriceComponent extends HTMLElement {
      constructor() {
        super();
        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.onCartLinesUpdate = this.onCartLinesUpdate.bind(this);
      }

      /** @type {HTMLInputElement | undefined} */
      input = undefined;

      /** @type {HTMLElement | undefined} */
      quantitySelector = undefined;

      /** @type {{ minimum_quantity: number, price: number }[]} */
      priceBreaks = [];

      #currentQuantity = undefined;
      #abortController = new AbortController();

      connectedCallback() {
        this.priceBreaks = this.getPriceBreaks();
        this.bindToInput();
        document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantityChange, {
          signal: this.#abortController.signal,
        });
        document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate, {
          signal: this.#abortController.signal,
        });
        this.render();
      }

      disconnectedCallback() {
        this.unbindFromInput();
        this.#abortController.abort();
      }

      onQuantityChange(event) {
        if (event?.detail && typeof event.detail.quantity === 'number') {
          this.#currentQuantity = event.detail.quantity;
        }

        if (event instanceof Event && event.target instanceof Element) {
          const scope = this.closest('product-form-component, product-card, dialog, .quick-reorder-item-form');
          if (scope && !scope.contains(event.target)) return;
        }

        this.bindToInput();
        this.render();
      }

      onCartLinesUpdate(event) {
        if (event.promise?.then) {
          event.promise.then(() => this.onQuantityChange()).catch(() => this.onQuantityChange());
        } else {
          this.onQuantityChange();
        }
      }

      bindToInput() {
        const scope = this.closest('product-form-component, product-card, dialog');
        const nextSelector = /** @type {HTMLElement | null} */ (
          scope?.querySelector('quantity-selector-component')
        );
        const nextInput = /** @type {HTMLInputElement | null} */ (
          scope?.querySelector(
            'quantity-selector-component input[ref="quantityInput"], input[name="quantity"]:not([type="hidden"])'
          )
        );

        if (this.input === nextInput && this.quantitySelector === nextSelector) return;

        this.unbindFromInput();
        this.quantitySelector = nextSelector ?? undefined;
        this.input = nextInput ?? undefined;

        if (this.quantitySelector) {
          this.quantitySelector.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantityChange);
        }

        if (!this.input) return;

        this.input.addEventListener('input', this.onQuantityChange);
        this.input.addEventListener('change', this.onQuantityChange);
      }

      unbindFromInput() {
        if (this.quantitySelector) {
          this.quantitySelector.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantityChange);
        }

        if (!this.input) return;

        this.input.removeEventListener('input', this.onQuantityChange);
        this.input.removeEventListener('change', this.onQuantityChange);
      }

      getPriceBreaks() {
        const priceBreaksScript = this.querySelector('[data-price-breaks]');
        if (!priceBreaksScript) return [];

        try {
          return JSON.parse(priceBreaksScript.textContent).sort(
            (left, right) => right.minimum_quantity - left.minimum_quantity
          );
        } catch (error) {
          console.error('Failed to parse total price price breaks.', error);
          return [];
        }
      }

      getEnteredQuantity() {
        if (typeof this.#currentQuantity === 'number') {
          return this.#currentQuantity;
        }

        const fallbackQuantity = parseInt(this.dataset.minQuantity || this.input?.dataset.min || this.input?.min || 1, 10);
        const enteredQuantity = parseInt(this.input?.value, 10);
        return Number.isNaN(enteredQuantity) ? fallbackQuantity : enteredQuantity;
      }

      getCartQuantity() {
        const cartQuantity = parseInt(this.input?.dataset.cartQuantity, 10);
        return Number.isNaN(cartQuantity) ? 0 : cartQuantity;
      }

      getBasePrice() {
        const basePrice = parseInt(this.dataset.basePrice, 10);
        return Number.isNaN(basePrice) ? 0 : basePrice;
      }

      getCompareAtPrice() {
        const compareAtPrice = parseInt(this.dataset.compareAtPrice, 10);
        return Number.isNaN(compareAtPrice) ? 0 : compareAtPrice;
      }

      getMoneyFormat() {
        return this.querySelector('template[ref="moneyFormat"]')?.content?.textContent?.trim() || '{{amount}}';
      }

      formatAmount(amount) {
        const currencyCode = this.dataset.currencyCode || 'USD';
        const showCurrencyCode = this.dataset.currencyEnabled === 'true';
        const formattedAmount = formatMoney(amount, this.getMoneyFormat(), currencyCode);

        return `
          <span class="price-total__amount">${formattedAmount}</span>
          ${showCurrencyCode ? `<span class="price-total__currency">${currencyCode}</span>` : ''}
        `;
      }

      getUnitPrice() {
        const effectiveQuantity = this.getCartQuantity() + this.getEnteredQuantity();

        for (const priceBreak of this.priceBreaks) {
          if (effectiveQuantity >= priceBreak.minimum_quantity) {
            return Number.parseInt(String(priceBreak.price), 10);
          }
        }

        return this.getBasePrice();
      }

      updateMoneyTarget(target, amount, strike = false) {
        if (!(target instanceof HTMLElement)) return;
        target.innerHTML = strike ? `<s class="price-total__strike">${this.formatAmount(amount)}</s>` : this.formatAmount(amount);
      }

      render() {
        const currentQuantity = this.getEnteredQuantity();
        const unitPrice = this.getUnitPrice();
        const compareAtPrice = this.getCompareAtPrice();
        const totalPrice = unitPrice * currentQuantity;
        const compareAtTotal = compareAtPrice > 0 ? compareAtPrice * currentQuantity : 0;
        const showCompareAt = compareAtPrice > unitPrice;

        const totalCurrent = this.querySelector('[data-total-cost-value], [data-total-price-current]');
        const unitCurrent = this.querySelector('[data-total-unit-current]');
        const unitOld = this.querySelector('[data-total-unit-old]');
        const totalOld = this.querySelector('[data-total-total-old]');

        this.updateMoneyTarget(totalCurrent, totalPrice);
        this.updateMoneyTarget(unitCurrent, unitPrice);

        if (unitOld instanceof HTMLElement) {
          unitOld.textContent = '';
          if (showCompareAt) {
            this.updateMoneyTarget(unitOld, compareAtPrice, true);
          }
        }

        if (totalOld instanceof HTMLElement) {
          totalOld.textContent = '';
          if (showCompareAt) {
            this.updateMoneyTarget(totalOld, compareAtTotal, true);
          }
        }
      }
    }
  );
}

if (!customElements.get('price-total')) {
  class PriceTotalComponent extends customElements.get('pdp-total-cost') {}

  customElements.define('price-total', PriceTotalComponent);
}
